import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import { WorkoutLayer } from '@strength/db-schema';
import { Tag, zTag } from '@strength/domain';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { z } from 'zod';
import { trpc } from '../../../../../trpc/react';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '../../../../components/form/form';
import { MultiSelect } from '../../../../components/input/multi-select';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { cn } from '../../../../components/utils';
import { useViewer } from '../../../../context/viewer';
import { useToast } from '../../../../hooks/useToast';
import {
  mergeTemplateLayers,
  WorkoutTemplateData,
} from '../workout-template-utils';
import { WorkoutTemplateSheet } from './WorkoutTemplateSheet';
import { updateDayAt, WorkoutTemplateDayList } from './WorkoutTemplateDayList';

interface WorkoutTemplateDrawerProps {
  mode: 'create' | 'edit';
  workoutTemplate: WorkoutTemplateData;
  tagOptions: Tag[];
  onClose: () => void;
  onDelete: (templateId: string, name: string) => void;
  onSave: (
    workoutTemplate: WorkoutTemplateData,
    saveInstructions: boolean,
  ) => void;
}

export const WorkoutTemplateDrawer = ({
  mode,
  workoutTemplate,
  tagOptions: _tagOptions,
  onClose,
  onDelete,
  onSave,
}: WorkoutTemplateDrawerProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    org: {
      settings: { enableTeamLibrary },
    },
    team: { selectedTeam, teams },
    hasPermissions,
  } = useViewer();

  const teamName = enableTeamLibrary
    ? teams.find((team) => team.id === selectedTeam)?.name
    : undefined;
  const form = useForm<WorkoutTemplateForm>({
    resolver: zodResolver(WorkoutTemplateFormSchema),
    defaultValues: getWorkoutTemplateForm(workoutTemplate),
  });
  const { handleSubmit, control } = form;
  const [saveInstructions, setSaveInstructions] = useState(true);
  const [tagOptions, setTagOptions] = useState<Tag[]>(_tagOptions);
  const name = form.watch('name');
  const [debouncedName, setDebouncedName] = useState(name);

  // Local working copy of template layers — only used in edit mode
  const [localTemplate, setLocalTemplate] = useState<WorkoutLayer[]>(
    workoutTemplate.template,
  );

  // Debounce the name field
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name);
    }, 600); // 600ms debounce delay

    return () => clearTimeout(timer);
  }, [name]);

  const hasWorkoutTemplateWithNameQuery =
    trpc.workoutTemplateLibrary.hasWorkoutTemplateWithName.useQuery({
      name: debouncedName,
      excludeId: mode === 'edit' ? workoutTemplate.id : undefined,
    });
  const hasWorkoutTemplateWithName =
    hasWorkoutTemplateWithNameQuery.data ?? false;

  const utils = trpc.useUtils();

  const editWorkoutTemplateMutation =
    trpc.workoutTemplateLibrary.editWorkoutTemplate.useMutation();

  const isEditableMode = mode === 'edit';
  // Library gating: the edit drawer gates on the Templates Edit/Delete keys;
  // the create drawer (block builder's save-as-template flow) on Create.
  const disableEdit =
    isEditableMode && !hasPermissions('library.templates', 'edit');
  const disableDelete =
    isEditableMode && !hasPermissions('library.templates', 'delete');
  const disableCreate =
    mode === 'create' && !hasPermissions('library.templates', 'create');

  // The explicit isMultiDay signal from the save path — the template array
  // length alone can't distinguish multi-day from a legacy layer stack.
  const isMultiDay = !!workoutTemplate.isMultiDay;

  // Multi-day drill-down: null shows the day list, an index shows that day's
  // sheet with a Back button.
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  // A day is drilled into (list hidden, that day's sheet shown) — create mode
  // drills in read-only, edit mode drills in editable.
  const isDayDrillDown = isMultiDay && selectedDayIndex !== null;

  // Leaving the drill-down view (switching templates) resets the selection
  useEffect(() => {
    setSelectedDayIndex(null);
  }, [workoutTemplate.id]);

  const onSubmitHandler = (formValues: WorkoutTemplateForm) => {
    if (isEditableMode) {
      editWorkoutTemplateMutation.mutate(
        buildEditPayload(
          workoutTemplate.id,
          formValues.name,
          formValues.tags,
          localTemplate,
          isMultiDay,
        ),
        {
          onSuccess: () => {
            utils.workoutTemplateLibrary.templateSearch.invalidate();
            toast({
              title: t('wasUpdated', { name: formValues.name }),
              severity: 'success',
            });
            onClose();
          },
          onError: () => {
            toast({ title: t('somethingWentWrong'), severity: 'error' });
          },
        },
      );
    } else {
      onSave(
        {
          ...formValues,
          template: workoutTemplate.template,
          isMultiDay: workoutTemplate.isMultiDay,
        },
        saveInstructions,
      );
      onClose();
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      anchor="right"
      {...(isEditableMode
        ? {
            PaperProps: {
              style: { width: '30%', minWidth: '360px' },
            },
          }
        : {})}
    >
      <DrawerHeader
        onClose={onClose}
        title={
          mode === 'create'
            ? t('createWorkoutTemplate')
            : t('editWorkoutTemplate')
        }
      >
        {teamName && (
          <Typography variant="body2" color="text.secondary">
            {teamName}
          </Typography>
        )}
      </DrawerHeader>
      <Form {...form}>
        <form
          id="workout-template-form"
          onSubmit={handleSubmit(onSubmitHandler)}
          className="flex flex-col gap-2 flex-grow overflow-hidden"
        >
          <div className="flex flex-col gap-2 grow p-6 pt-0 overflow-y-auto">
            {/* Day drill-down shows only that day's sheet — template-level
                fields (name, tags) belong to the list view */}
            {isDayDrillDown ? (
              <>
                {workoutTemplate.template[selectedDayIndex] && (
                  <WorkoutTemplateSheet
                    layers={[
                      localTemplate[selectedDayIndex] ??
                        workoutTemplate.template[selectedDayIndex],
                    ]}
                    // templates store the compact sets-metric draft form,
                    // so the Sets column carries a real prescription — show
                    // it in read-only previews too (create mode)
                    showSetsColumn={true}
                    readOnly={!isEditableMode}
                    onLayersChange={
                      isEditableMode
                        ? (updated) => {
                            const first = updated[0];
                            if (!first) return;
                            // sheet only sees one day — splice its layer back
                            // into the full day list
                            setLocalTemplate((prev) =>
                              updateDayAt(prev, selectedDayIndex, first),
                            );
                          }
                        : undefined
                    }
                  />
                )}
              </>
            ) : (
              <>
                {hasWorkoutTemplateWithName && (
                  <Alert severity="error">
                    <AlertTitle>
                      {t('templateNameMustBeUnique', { name })}
                    </AlertTitle>
                  </Alert>
                )}
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <TextField
                        label={t('name')}
                        {...field}
                        fullWidth
                        disabled={disableEdit}
                        error={hasWorkoutTemplateWithName}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="tags"
                  render={({ field: { value, onChange } }) => (
                    <FormItem className="flex flex-col">
                      <FormControl disabled={disableEdit}>
                        <MultiSelect
                          label={t('tags')}
                          placeholder={t('selectTags')}
                          disabled={disableEdit}
                          selectedOptions={value}
                          options={tagOptions}
                          onChange={onChange}
                          optionToName={(option) => option.name}
                          createOption={(name) => {
                            const newTag = {
                              id: v4(),
                              name,
                              type: 'workout_template' as const,
                            };
                            setTagOptions([...tagOptions, newTag]);
                            return newTag;
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {mode === 'create' && (
                  <div className="flex flex-row items-center">
                    <Checkbox
                      checked={saveInstructions}
                      className="pl-0"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSaveInstructions(true);
                        } else {
                          setSaveInstructions(false);
                        }
                      }}
                    />
                    <Typography>{t('saveInstructions')}</Typography>
                  </div>
                )}
                {workoutTemplate.template[0] &&
                  (isMultiDay ? (
                    <WorkoutTemplateDayList
                      layers={
                        isEditableMode
                          ? localTemplate
                          : workoutTemplate.template
                      }
                      readOnly={!isEditableMode}
                      onChange={setLocalTemplate}
                      onSelectDay={setSelectedDayIndex}
                    />
                  ) : (
                    <WorkoutTemplateSheet
                      layers={
                        isEditableMode
                          ? localTemplate
                          : workoutTemplate.template
                      }
                      // templates store the compact sets-metric draft form,
                      // so the Sets column carries a real prescription — show
                      // it in read-only previews too (create mode)
                      showSetsColumn={true}
                      readOnly={!isEditableMode}
                      onLayersChange={
                        isEditableMode ? setLocalTemplate : undefined
                      }
                    />
                  ))}
              </>
            )}
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          mode === 'edit' || isDayDrillDown ? 'justify-between' : 'justify-end',
          'flex flex-row gap-2 w-full p-8',
        )}
      >
        {isDayDrillDown ? (
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedDayIndex(null);
            }}
          >
            {t('back')}
          </Button>
        ) : (
          mode === 'edit' && (
            <DisabledWithTooltip
              disabled={disableDelete}
              reason={t('permissionDisabled.delete', { item: t('templates') })}
            >
              <Button
                variant="containedDestructive"
                onClick={() => {
                  onDelete(workoutTemplate.id, name);
                  onClose();
                }}
              >
                {t('delete')}
              </Button>
            </DisabledWithTooltip>
          )
        )}
        <div className="flex gap-2">
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <DisabledWithTooltip
            disabled={disableCreate}
            reason={t('permissionDisabled.create', { item: t('templates') })}
          >
            <Button
              type="submit"
              form="workout-template-form"
              disabled={
                !form.formState.isValid ||
                hasWorkoutTemplateWithName ||
                disableEdit ||
                disableCreate
              }
            >
              {t('save')}
            </Button>
          </DisabledWithTooltip>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

const WorkoutTemplateFormSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  tags: z.array(zTag),
});

type WorkoutTemplateForm = z.infer<typeof WorkoutTemplateFormSchema>;

function buildEditPayload(
  id: string,
  name: string,
  tags: Tag[],
  template: WorkoutLayer[],
  isMultiDay: boolean,
) {
  return {
    id,
    name,
    tags: tags.map((tag, i) => ({ id: v4(), sortOrder: i, tag })),
    // multi-day templates store one merged layer per day — send verbatim (a
    // reorder is just a different entry order) and flag it so the server keeps
    // the day boundaries; single-day stacks still merge. Post-Phase-1 storage
    // keeps exactly one entry per day, so entry count > 1 means multi-day.
    isMultiDay,
    template: isMultiDay ? template : mergeTemplateLayers(template),
  };
}

const getWorkoutTemplateForm = (
  workoutTemplate: WorkoutTemplateData,
): WorkoutTemplateForm => {
  return {
    id: workoutTemplate.id,
    name: workoutTemplate.name,
    tags: workoutTemplate.tags,
  };
};
